import cls from 'classnames';
import invert from 'lodash/invert';
import * as React from 'react';
import { Spin } from 'ssc-ui-react';

import { $gt } from '@/common/appCtx';
import { useWorkforceEnums } from '@/store/workforce-enums';

import { OriginGroupItem } from '../../defined';
import { transformNum } from '../../utils/common';
import Empty from '../empty';
import styles from './index.module.less';

interface ThroughputProps {
  order?: number;
  workstations?: number;
  operators?: number;
  scanList?: OriginGroupItem['interactive_type_throughput_list'];
  nonScanList?: OriginGroupItem['non_interactive_type_throughput_list'];
  loading?: boolean;
}

const Throughput: React.FC<ThroughputProps> = (props: ThroughputProps) => {
  const enums = useWorkforceEnums.use.enums();
  const workstationTypeMap = React.useMemo(() => invert(enums['workstation_type']), [enums]);

  const renderActivityItem = (
    list: OriginGroupItem['interactive_type_throughput_list'] | OriginGroupItem['non_interactive_type_throughput_list'],
    title: string,
  ) => {
    return (
      <div className={styles.activityItem}>
        <div className={styles.desc}>{title}</div>
        <Spin
          size="large"
          spinning={props.loading}
          className={styles.scan}
        >
          {list?.map((item, index) => (
            <div
              key={index}
              className={cls(styles.line, { [styles.bold]: item.is_calculate_type })}
            >
              <div
                className={styles.left}
                title={workstationTypeMap[item.activity_type ?? '']}
              >
                <span className={styles.ellipsis}>{workstationTypeMap[item.activity_type ?? '']}</span>
                <span>:</span>
              </div>
              <div className={styles.right}>
                <div className={styles.ellipsis}>
                  <span className={styles.value}>{transformNum(item.throughput)}</span>
                  {!!item.is_calculate_type && (
                    <span className={styles.progress}>&nbsp;{$gt('[In Calculation]')}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
          {!list?.length && (
            <div className={styles.empty}>
              <Empty />
            </div>
          )}
        </Spin>
      </div>
    );
  };

  const renderThroughputItem = () => {
    return (
      <>
        <Spin
          size="large"
          spinning={props.loading}
          className={styles.throughputItem}
        >
          <div className={styles.title}>
            {$gt('Throughput')}
          </div>
          <div className={styles.order}>
            <span className={styles.orderNum}>{props.order ? transformNum(props.order) : '-'}</span>
            <span className={styles.orderDesc}>{$gt('Orders')}</span>
          </div>
          <div className={styles.count}>
            <div className={styles.workstations}>
              {$gt('Workstations:')} {props.workstations ? props.workstations : '-'}
            </div>
            <div className={styles.operators}>
              {$gt('Operators:')} {props.operators ? props.operators : '-'}
            </div>
          </div>
        </Spin>
        <div className={styles.divider}></div>
      </>
    );
  };

  return (
    <div className={styles.throughput}>
      <div className={styles.box}>
        {renderThroughputItem()}
        <div className={styles.activity}>
          <div className={styles.title}>
            {$gt('Activity Type')}
          </div>
          <div className={styles.activityType}>
            {renderActivityItem(props.scanList, $gt('Scan'))}
            {renderActivityItem(props.nonScanList, $gt('No Scan'))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Throughput;
